import { supabase } from './supabaseClient';
import { getNegocioId } from './negocioActual';
import { obtenerProductos } from './productosService';
import { obtenerCategoriasGasto } from './categoriasGastoService';
import { contarPedidosPendientes } from './pedidosService';
import { obtenerClientes } from './clientesService';
import { mesAnterior } from '../utils/metas';

// 🎯 Mis metas (sql/125).

// ---- Meta del mes ----
export async function obtenerMeta(mes) {
  const { data, error } = await supabase.from('metas_mes').select('*').eq('negocio_id', getNegocioId()).eq('mes', mes).maybeSingle();
  if (error) throw error;
  return data;
}

export async function guardarMeta(mes, { metaVentas, diasVenta, motivo, presupuesto }) {
  const fila = {
    negocio_id: getNegocioId(), mes,
    meta_ventas: Math.max(0, Number(metaVentas) || 0),
    dias_venta: (diasVenta && diasVenta.length) ? diasVenta : [1, 2, 3, 4, 5, 6],
    motivo: motivo?.trim().slice(0, 300) || null,
    presupuesto: presupuesto || {},
    actualizado_en: new Date().toISOString(),
  };
  const { data, error } = await supabase.from('metas_mes').upsert([fila], { onConflict: 'negocio_id,mes' }).select().single();
  if (error) throw error;
  return data;
}

// ---- Estrategias ----
export async function obtenerEstrategias(mes) {
  const { data, error } = await supabase.from('estrategias').select('*').eq('negocio_id', getNegocioId()).eq('mes', mes).order('creado_en');
  if (error) throw error;
  return data || [];
}

export async function crearEstrategia(mes, { titulo, detalle, origen = 'propia' }) {
  const { data, error } = await supabase.from('estrategias').insert([{
    negocio_id: getNegocioId(), mes, titulo: titulo.trim().slice(0, 140), detalle: detalle?.trim().slice(0, 1000) || null, origen,
  }]).select().single();
  if (error) throw error;
  return data;
}

export async function cambiarEstadoEstrategia(id, estado) {
  const { error } = await supabase.from('estrategias').update({ estado, hecha_en: estado === 'hecha' ? new Date().toISOString() : null }).eq('id', id);
  if (error) throw error;
}

export async function eliminarEstrategia(id) {
  const { error } = await supabase.from('estrategias').delete().eq('id', id);
  if (error) throw error;
}

// ---- Recordatorios ----
export async function obtenerRecordatorios() {
  const desde = new Date(Date.now() - 30 * 86400000).toISOString();
  const { data, error } = await supabase.from('recordatorios').select('*').eq('negocio_id', getNegocioId())
    .or(`hecho.eq.false,cuando.gte.${desde}`).order('cuando').limit(300);
  if (error) throw error;
  return data || [];
}

export async function crearRecordatorio({ titulo, nota, cuando, repetir, estrategiaId }) {
  const { data, error } = await supabase.from('recordatorios').insert([{
    negocio_id: getNegocioId(), titulo: titulo.trim().slice(0, 140), nota: nota?.trim().slice(0, 500) || null,
    cuando: new Date(cuando).toISOString(), repetir: repetir || null, estrategia_id: estrategiaId || null,
  }]).select().single();
  if (error) throw error;
  return data;
}

export async function marcarRecordatorio(id, hecho) {
  const { error } = await supabase.from('recordatorios').update({ hecho }).eq('id', id);
  if (error) throw error;
}

export async function eliminarRecordatorio(id) {
  const { error } = await supabase.from('recordatorios').delete().eq('id', id);
  if (error) throw error;
}

// ---- Datos del negocio para las cuentas (este mes + 3 anteriores) ----
async function paginado(consulta) {
  let desde = 0; let todas = []; let seguir = true;
  while (seguir) {
    const { data, error } = await consulta().range(desde, desde + 999);
    if (error) throw error;
    todas = todas.concat(data || []);
    desde += 1000;
    seguir = (data || []).length === 1000 && desde < 30000;
  }
  return todas;
}

export async function obtenerDatosMetas(mes) {
  const neg = getNegocioId();
  const inicio = mesAnterior(mes, 3);
  const [a, m, d] = inicio.split('-').map(Number);
  const inicioISO = new Date(a, m - 1, d).toISOString();
  const [ventas, libres, gastos, productos, categoriasGasto, pedidosPendientes, clientes, usoIA, deudas] = await Promise.all([
    paginado(() => supabase.from('ventas').select('vendido_en, total, cantidad, costo_unitario, nombre_producto, producto_id, cliente_id, ticket_id')
      .eq('negocio_id', neg).gte('vendido_en', inicioISO).order('vendido_en')),
    paginado(() => supabase.from('ingresos_libres').select('fecha, monto, costo').eq('negocio_id', neg).gte('fecha', inicio).order('fecha')),
    paginado(() => supabase.from('gastos').select('fecha, monto, categoria').eq('negocio_id', neg).gte('fecha', inicio).order('fecha')),
    obtenerProductos().catch(() => []),
    obtenerCategoriasGasto().catch(() => []),
    contarPedidosPendientes().catch(() => 0),
    obtenerClientes().catch(() => []),
    supabase.from('metas_ia_uso').select('usos').eq('negocio_id', neg).eq('mes', mes).maybeSingle().then((r) => r.data?.usos || 0, () => 0),
    supabase.from('deudas').select('*').eq('negocio_id', neg).then((r) => r.data || [], () => []),
  ]);
  return { ventas, libres, gastos, productos, categoriasGasto, pedidosPendientes, clientes, usoIA, deudas };
}

// Lo justo para la tarjeta de Inicio (más liviano que obtenerDatosMetas).
export async function obtenerResumenInicio(mes) {
  const neg = getNegocioId();
  const [a, m] = mes.split('-').map(Number);
  const hace12 = new Date(); hace12.setDate(hace12.getDate() - 12); hace12.setHours(0, 0, 0, 0);
  const desde = new Date(Math.min(new Date(a, m - 1, 1).getTime(), hace12.getTime()));
  const desdeYMD = `${desde.getFullYear()}-${String(desde.getMonth() + 1).padStart(2, '0')}-${String(desde.getDate()).padStart(2, '0')}`;
  const finHoy = new Date(); finHoy.setHours(23, 59, 59, 999);
  const [meta, ventas, libres, gastos, recordatorios] = await Promise.all([
    obtenerMeta(mes),
    paginado(() => supabase.from('ventas').select('vendido_en, total, ticket_id').eq('negocio_id', neg).gte('vendido_en', desde.toISOString()).order('vendido_en')),
    paginado(() => supabase.from('ingresos_libres').select('fecha, monto').eq('negocio_id', neg).gte('fecha', desdeYMD).order('fecha')),
    paginado(() => supabase.from('gastos').select('fecha, monto, categoria').eq('negocio_id', neg).gte('fecha', mes).order('fecha')),
    supabase.from('recordatorios').select('id, titulo, cuando, hecho').eq('negocio_id', neg).eq('hecho', false)
      .lte('cuando', finHoy.toISOString()).order('cuando').limit(20).then((r) => r.data || [], () => []),
  ]);
  return { meta, ventas, libres, gastos, recordatorios };
}

// 📅 ¿Qué hago hoy? (Edge Function plan-metas, modo "hoy"; cupo por plan: 1/2/3 al día, sql/130).
export async function armarPlanHoy(resumen) {
  const { data, error } = await supabase.functions.invoke('plan-metas', { body: { negocioId: getNegocioId(), modo: 'hoy', resumen } });
  if (error) throw error;
  if (data?.error) { const e = new Error(data.error); e.codigo = data.codigo; throw e; }
  return data;
}

// ✨ Plan con IA (Edge Function plan-metas). `resumen` lo arma la pantalla.
export async function armarPlanIA(resumen) {
  const { data, error } = await supabase.functions.invoke('plan-metas', { body: { negocioId: getNegocioId(), resumen } });
  if (error) throw error;
  if (data?.error) { const e = new Error(data.error); e.codigo = data.codigo; throw e; }
  return data;
}

// ---- 🧭 Coach · Mis deudas (sql/132) ----
export async function obtenerDeudas() {
  const { data, error } = await supabase.from('deudas').select('*').eq('negocio_id', getNegocioId()).order('creado_en');
  if (error) throw error;
  return data || [];
}

const numeroONulo = (v) => (v === '' || v == null || !Number.isFinite(Number(v)) ? null : Number(v));
export async function guardarDeuda(d) {
  const fila = {
    negocio_id: getNegocioId(), nombre: String(d.nombre || '').trim().slice(0, 80) || 'Deuda', tipo: d.tipo || 'tarjeta',
    saldo: numeroONulo(d.saldo) ?? 0, saldo_usd: numeroONulo(d.saldo_usd) ?? 0, tipo_cambio: numeroONulo(d.tipo_cambio),
    tasa_anual: numeroONulo(d.tasa_anual), pago_minimo: numeroONulo(d.pago_minimo), pago_minimo_usd: numeroONulo(d.pago_minimo_usd), cargos_mes: numeroONulo(d.cargos_mes), dia_pago: numeroONulo(d.dia_pago),
    del_negocio: d.del_negocio !== false, nota: d.nota?.trim().slice(0, 300) || null, actualizado_en: new Date().toISOString(),
  };
  const q = d.id ? supabase.from('deudas').update(fila).eq('id', d.id) : supabase.from('deudas').insert([fila]);
  const { data, error } = await q.select().single();
  if (error) throw error;
  return data;
}

export async function eliminarDeuda(id) {
  const { error } = await supabase.from('deudas').delete().eq('id', id);
  if (error) throw error;
}

// ---- 🗓️ Coach · revisión de la semana (sql/135) ----
export async function obtenerRevisiones(limite = 6) {
  const { data, error } = await supabase.from('revisiones_semana').select('*').eq('negocio_id', getNegocioId())
    .order('semana', { ascending: false }).limit(limite);
  if (error) throw error;
  return data || [];
}

export async function guardarRevision({ semana, nota, separeTuyo, separeDeudas }) {
  const { data, error } = await supabase.from('revisiones_semana').upsert([{
    negocio_id: getNegocioId(), semana, nota: nota?.trim().slice(0, 1000) || null,
    separe_tuyo: !!separeTuyo, separe_deudas: !!separeDeudas, actualizado_en: new Date().toISOString(),
  }], { onConflict: 'negocio_id,semana' }).select().single();
  if (error) throw error;
  return data;
}
